import { parse } from 'edtf';
import { isNumber, isObject } from 'lodash-es';
import { EDTFType } from '../entities/edtf-type.enum';
import {
    COMPLETELY_UNSPECIFIED_EDTF_FORMAT,
    EDTFValidation,
    EDTFValidationType
} from '../entities/edtf-validation.entity';

export function validateEDTF(dateString: string): EDTFValidation | null {
    try {
        return parse(dateString);
    } catch (exception) {
        return null;
    }
}

export function isValidEDTF(dateString: string, edtfType: EDTFType): boolean {
    return detectEDTFType(dateString) === edtfType;
}

export function detectEDTFType(dateString: string): EDTFType | null {
    const validation: EDTFValidation | null = validateEDTF(dateString);

    /*
     *  MED-2373: Unspecified EDTF type support
     *  This is a non-official type of EDTF we added manually
     */
    if (dateString === COMPLETELY_UNSPECIFIED_EDTF_FORMAT) {
        return EDTFType.COMPLETELY_UNSPECIFIED;
    }

    if (!validation) {
        return null;
    }

    if (validation.level === 0) {
        return detectLvl0EDTFType(validation);
    }

    if (validation.level === 1) {
        return detectLvl1EDTFType(validation);
    }

    if (validation.level === 2) {
        return detectLvl2EDTFType();
    }

    return null;
}

function detectLvl0EDTFType(validation: EDTFValidation): EDTFType | null {
    const numberValues: number = validation.values.filter(isNumber).length;

    if (validation.type === EDTFValidationType.DATE) {
        if (numberValues !== validation.values.length) {
            return null;
        }

        if (numberValues >= 6) {
            return detectDateTimeEDTFTime(validation);
        }

        switch (numberValues) {
            case 1: {
                const positiveValue: boolean = isNumber(validation.values[0]) && validation.values[0] > 0;
                return positiveValue ? EDTFType.DATE_PRECISION_YEAR : EDTFType.NEGATIVE_CALENDER_YEAR;
            }

            case 2: {
                return EDTFType.DATE_PRECISION_YEAR_AND_MONTH;
            }

            case 3: {
                return EDTFType.DATE_COMPLETE;
            }

            default: {
                return null;
            }
        }
    }

    const lvl0Values: number = validation.values
        .filter(isEDTFValidation)
        .filter(value => detectLvl0EDTFType(value as EDTFValidation)).length;

    if (validation.type === EDTFValidationType.INTERVAL && validation.values.length === 2 && lvl0Values === 2) {
        return EDTFType.TIME_INTERVAL;
    }

    return null;
}

function detectDateTimeEDTFTime(validation: EDTFValidation): EDTFType | null {
    if (typeof validation.offset === 'undefined') {
        return null;
    }
    if (validation.offset === 0) {
        return EDTFType.DATETIME_COMPLETE_UTC;
    }

    if (new Date().getTimezoneOffset() === validation.offset) {
        return EDTFType.DATETIME_COMPLETE;
    }
    return EDTFType.DATETIME_COMPLETE_SHIFT;
}

function detectLvl1EDTFType(validation: EDTFValidation): EDTFType | null {
    const numberValues: number = validation.values.filter(isNumber).length;

    if (validation.type === EDTFValidationType.YEAR && validation.values.length === 1 && numberValues === 1) {
        return EDTFType.LETTER_PREFIXED_YEAR;
    }

    if (validation.type === EDTFValidationType.SEASON && validation.values.length === 2 && numberValues === 2) {
        return EDTFType.SEASONS;
    }

    if (validation.type === EDTFValidationType.DATE) {
        return detectLvl1EDTFDateType(validation);
    }

    if (validation.type === EDTFValidationType.INTERVAL) {
        return detectLvl1EDTFIntervalType(validation);
    }

    return null;
}

function detectLvl1EDTFDateType(validation: EDTFValidation): EDTFType | null {
    if (validation.uncertain && validation.approximate) {
        return EDTFType.DATE_QUALIFICATION_UNCERTAIN_APPROXIMATE;
    }
    if (validation.uncertain) {
        return EDTFType.DATE_QUALIFICATION_UNCERTAIN;
    }
    if (validation.approximate) {
        return EDTFType.DATE_QUALIFICATION_APPROXIMATE;
    }
    if (validation.unspecified) {
        switch (validation.unspecified) {
            case 8:
                return EDTFType.UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DECADE;
            case 12:
                return EDTFType.UNSPECIFIED_DIGIT_FROM_THE_RIGHT_CENTURY;
            case 48:
                return EDTFType.UNSPECIFIED_DIGIT_FROM_THE_RIGHT_MONTH;
            case 192:
                return EDTFType.UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DAY_IN_MONTH;
            case 240:
                return EDTFType.UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DAY_IN_YEAR;
            default:
                return null;
        }
    }

    return null;
}

function detectLvl1EDTFIntervalType(validation: EDTFValidation): EDTFType | null {
    const dateValidationValues: number = validation.values
        .filter(isEDTFValidation)
        .filter(value => (value as EDTFValidation).type === EDTFValidationType.DATE).length;

    if (validation.values.length !== 2) {
        return null;
    }

    if (dateValidationValues === 2) {
        return EDTFType.EXTENDED_INTERVAL;
    }

    if (validation.values[0] === null && dateValidationValues === 1) {
        return EDTFType.EXTENDED_INTERVAL_UNKNOWN_BEGIN;
    }

    if (validation.values[1] === null && dateValidationValues === 1) {
        return EDTFType.EXTENDED_INTERVAL_UNKNOWN_END;
    }

    if (validation.values[0] === Infinity && dateValidationValues === 1) {
        return EDTFType.EXTENDED_INTERVAL_OPEN_BEGIN;
    }

    if (validation.values[1] === Infinity && dateValidationValues === 1) {
        return EDTFType.EXTENDED_INTERVAL_OPEN_END;
    }

    return null;
}

function detectLvl2EDTFType(): EDTFType | null {
    // UNSUPPORTED
    return null;
}

function isEDTFValidation(validation: any): boolean {
    if (!isObject(validation)) {
        return false;
    }

    const keys: string[] = Object.keys(validation);
    return keys.includes('type') && keys.includes('level') && keys.includes('values');
}
